import { mailGatewayHost } from '../api'

const MAIL_API_KEY = '78fb6eea62d57efe25efe98c112a6264'

const MAIL_ENABLED_HOSTS = [
  'staging.rolzo.com',
  'business.rolzo.com',
  // 'localhost',
]
const isMailEnabled = () =>
  MAIL_ENABLED_HOSTS.includes(window.location.hostname)

export const MAIL_SET_THREADS = 'MAIL_SET_THREADS'
export const MAIL_APPEND_THREADS = 'MAIL_APPEND_THREADS'
export const MAIL_MERGE_THREADS = 'MAIL_MERGE_THREADS'
export const MAIL_SET_LOADING_MORE = 'MAIL_SET_LOADING_MORE'
export const MAIL_SET_MESSAGES = 'MAIL_SET_MESSAGES'
export const MAIL_SET_SELECTED = 'MAIL_SET_SELECTED'
export const MAIL_APPEND_MESSAGE = 'MAIL_APPEND_MESSAGE'
export const MAIL_SET_LOADING = 'MAIL_SET_LOADING'
export const MAIL_UPDATE_THREAD = 'MAIL_UPDATE_THREAD'
export const MAIL_SET_ACCOUNTS = 'MAIL_SET_ACCOUNTS'
export const MAIL_SET_ACCOUNT_MANAGER_OPEN = 'MAIL_SET_ACCOUNT_MANAGER_OPEN'
export const MAIL_WS_CONNECTED = 'MAIL_WS_CONNECTED'
export const MAIL_WS_DISCONNECTED = 'MAIL_WS_DISCONNECTED'
export const MAIL_DELETE_THREAD = 'MAIL_DELETE_THREAD'
export const MAIL_SET_CHANNEL_UNREADS = 'MAIL_SET_CHANNEL_UNREADS'
export const MAIL_SET_TAB_COUNTS = 'MAIL_SET_TAB_COUNTS'

/**
 * Direct fetch to mail gateway — bypasses ApiService.handleResponse
 * since the gateway returns { data, pagination } without meta.success wrapper.
 */
const mailFetch = async (url, options = {}) => {
  if (!isMailEnabled()) return {}
  const resp = await fetch(`${mailGatewayHost}/${url}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': MAIL_API_KEY,
      ...(options.headers || {}),
    },
  })
  const data = await resp.json()
  if (!resp.ok) {
    const err = new Error(data.error || `Request failed (${resp.status})`)
    err.status = resp.status
    throw err
  }
  return data
}

// ── Thread actions ──

const buildMailThreadsQuery = (params = {}) => {
  const query = new URLSearchParams()
  if (params.orgId) query.set('orgId', params.orgId)
  if (params.channel) query.set('channel', params.channel)
  if (params.status) query.set('status', params.status)
  if (params.search) query.set('q', params.search)
  if (params.urgent !== undefined) query.set('urgent', params.urgent)
  if (params.type) query.set('type', params.type)
  if (params.ownerId) query.set('ownerId', params.ownerId)
  if (params.accountId) query.set('accountId', params.accountId)
  if (params.page) query.set('page', params.page)
  if (params.limit) query.set('limit', params.limit || 50)
  return query
}

export const fetchMailThreads = (params = {}) => async dispatch => {
  try {
    dispatch({ type: MAIL_SET_LOADING, payload: true })

    const query = buildMailThreadsQuery(params)
    const result = await mailFetch(`threads?${query.toString()}`)

    const threads = result.data || []
    const pagination = result.pagination || {}
    const counts = result.counts || null

    dispatch({
      type: MAIL_SET_THREADS,
      payload: { threads, pagination },
    })
    if (counts) {
      dispatch({ type: MAIL_SET_TAB_COUNTS, payload: counts })
    }
  } catch (err) {
    console.error('fetchMailThreads error:', err)
  } finally {
    dispatch({ type: MAIL_SET_LOADING, payload: false })
  }
}

export const loadMoreMailThreads = (extraParams = {}) => async (
  dispatch,
  getState
) => {
  const state = getState().mail
  if (!state) return
  if (state.loadingMore) return
  const { threadPagination = {} } = state
  const currentPage = threadPagination.page || 1
  const totalPages = threadPagination.pages || 1
  if (currentPage >= totalPages) return

  try {
    dispatch({ type: MAIL_SET_LOADING_MORE, payload: true })
    const query = buildMailThreadsQuery({
      ...extraParams,
      page: currentPage + 1,
      limit: threadPagination.limit || extraParams.limit || 50,
    })
    const result = await mailFetch(`threads?${query.toString()}`)
    const threads = result.data || []
    const nextPagination = result.pagination || {}
    const counts = result.counts || null
    dispatch({
      type: MAIL_APPEND_THREADS,
      payload: { threads, pagination: nextPagination },
    })
    if (counts) {
      dispatch({ type: MAIL_SET_TAB_COUNTS, payload: counts })
    }
  } catch (err) {
    console.error('loadMoreMailThreads error:', err)
  } finally {
    dispatch({ type: MAIL_SET_LOADING_MORE, payload: false })
  }
}

export const mergeMailThreads = (params = {}) => async dispatch => {
  try {
    const query = buildMailThreadsQuery({ ...params, page: 1 })
    const result = await mailFetch(`threads?${query.toString()}`)
    const threads = result.data || []
    const pagination = result.pagination || {}
    const counts = result.counts || null
    dispatch({
      type: MAIL_MERGE_THREADS,
      payload: { threads, pagination },
    })
    if (counts) {
      dispatch({ type: MAIL_SET_TAB_COUNTS, payload: counts })
    }
  } catch (err) {
    console.error('mergeMailThreads error:', err)
  }
}

export const fetchMailUnreadCounts = (orgId = 'rolzo') => async dispatch => {
  try {
    const result = await mailFetch(`unread-counts?orgId=${orgId}`)
    dispatch({
      type: MAIL_SET_CHANNEL_UNREADS,
      payload: result.data || { client: 0, partner: 0 },
    })
  } catch (err) {
    console.error('fetchMailUnreadCounts error:', err)
  }
}

export const fetchMailMessages = threadId => async dispatch => {
  try {
    const result = await mailFetch(`threads/${threadId}/messages`)
    const messages = result.data || []

    dispatch({
      type: MAIL_SET_MESSAGES,
      payload: messages,
    })
  } catch (err) {
    console.error('fetchMailMessages error:', err)
  }
}

export const sendMailReply = (threadId, body) => async dispatch => {
  try {
    const result = await mailFetch(`threads/${threadId}/send`, {
      method: 'POST',
      body: JSON.stringify(body),
    })
    // Message arrives via WebSocket (mail:message event)
    return result
  } catch (err) {
    console.error('sendMailReply error:', err)
    throw err
  }
}

export const forwardEmail = (threadId, body) => async dispatch => {
  try {
    const result = await mailFetch(`threads/${threadId}/send`, {
      method: 'POST',
      body: JSON.stringify(body),
    })
    return result
  } catch (err) {
    console.error('forwardEmail error:', err)
    throw err
  }
}

export const updateMailThread = (threadId, patch) => async dispatch => {
  try {
    const result = await mailFetch(`threads/${threadId}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    })

    const updated = result.data || {}
    dispatch({ type: MAIL_UPDATE_THREAD, payload: updated })
    return updated
  } catch (err) {
    console.error('updateMailThread error:', err)
    throw err
  }
}

export const setSelectedMailThread = thread => ({
  type: MAIL_SET_SELECTED,
  payload: thread,
})

// ── Note actions ──

export const addMailNote = (threadId, data) => async dispatch => {
  try {
    const result = await mailFetch(`threads/${threadId}/notes`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
    dispatch({ type: MAIL_UPDATE_THREAD, payload: result.data || {} })
    return result.data
  } catch (err) {
    console.error('addMailNote error:', err)
  }
}

export const editMailNote = (noteId, data) => async dispatch => {
  try {
    const result = await mailFetch(`notes/${noteId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    })
    dispatch({ type: MAIL_UPDATE_THREAD, payload: result.data || {} })
    return result.data
  } catch (err) {
    console.error('editMailNote error:', err)
  }
}

export const deleteMailNote = (noteId, data) => async dispatch => {
  try {
    const result = await mailFetch(`notes/${noteId}`, {
      method: 'DELETE',
      body: JSON.stringify(data),
    })
    dispatch({ type: MAIL_UPDATE_THREAD, payload: result.data || {} })
    return result.data
  } catch (err) {
    console.error('deleteMailNote error:', err)
  }
}

// ── Draft actions ──

export const saveMailDraft = (threadId, data) => async dispatch => {
  try {
    const result = await mailFetch(`threads/${threadId}/draft`, {
      method: 'PUT',
      body: JSON.stringify(data),
    })
    const updated = result.data || {}
    dispatch({ type: MAIL_UPDATE_THREAD, payload: updated })
    return updated
  } catch (err) {
    console.error('saveMailDraft error:', err)
    throw err
  }
}

export const deleteMailDraft = threadId => async dispatch => {
  try {
    const result = await mailFetch(`threads/${threadId}/draft`, {
      method: 'DELETE',
    })
    const updated = result.data || {}
    dispatch({ type: MAIL_UPDATE_THREAD, payload: updated })
    return updated
  } catch (err) {
    console.error('deleteMailDraft error:', err)
    throw err
  }
}

// ── Standalone compose draft actions ──

export const createMailDraft = data => async dispatch => {
  try {
    const result = await mailFetch('drafts', {
      method: 'POST',
      body: JSON.stringify(data),
    })
    const created = result.data || {}
    dispatch({ type: MAIL_UPDATE_THREAD, payload: created })
    return created
  } catch (err) {
    console.error('createMailDraft error:', err)
    throw err
  }
}

export const updateStandaloneDraft = (threadId, data) => async dispatch => {
  try {
    const result = await mailFetch(`drafts/${threadId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    })
    const updated = result.data || {}
    dispatch({ type: MAIL_UPDATE_THREAD, payload: updated })
    return updated
  } catch (err) {
    console.error('updateStandaloneDraft error:', err)
    throw err
  }
}

export const deleteStandaloneDraft = threadId => async dispatch => {
  try {
    await mailFetch(`drafts/${threadId}`, { method: 'DELETE' })
    dispatch({ type: MAIL_DELETE_THREAD, payload: threadId })
  } catch (err) {
    console.error('deleteStandaloneDraft error:', err)
    throw err
  }
}

export const sendStandaloneDraft = (threadId, body) => async dispatch => {
  try {
    const result = await mailFetch(`drafts/${threadId}/send`, {
      method: 'POST',
      body: JSON.stringify(body),
    })
    return result
  } catch (err) {
    console.error('sendStandaloneDraft error:', err)
    throw err
  }
}

export const sendComposeEmail = data => async dispatch => {
  try {
    const result = await mailFetch('compose/send', {
      method: 'POST',
      body: JSON.stringify(data),
    })
    return result
  } catch (err) {
    console.error('sendComposeEmail error:', err)
    throw err
  }
}

// ── Recipient search ──

export const searchRecipients = async (query, opts = {}) => {
  if (!query || query.length < 2) return []
  const { companyId, channel } =
    typeof opts === 'string'
      ? { companyId: opts } // backward compat: searchRecipients(q, companyId)
      : opts
  if (!companyId && !channel) return []
  try {
    const params = new URLSearchParams()
    params.set('q', query)
    if (companyId) params.set('companyId', companyId)
    if (channel) params.set('channel', channel)
    params.set('limit', '20')
    const result = await mailFetch(`recipients/search?${params.toString()}`)
    return result.data || []
  } catch (err) {
    console.error('searchRecipients error:', err)
    return []
  }
}

// ── Template actions ──

export const fetchAccountTemplates = accountId => async () => {
  try {
    const result = await mailFetch(
      `accounts/${accountId}/templates?refresh=true`
    )
    return result.data || []
  } catch (err) {
    console.error('fetchAccountTemplates error:', err)
    return []
  }
}

export const fetchTemplateContent = (accountId, templateId) => async () => {
  try {
    const result = await mailFetch(
      `accounts/${accountId}/templates/${templateId}/content`
    )
    return result.data || {}
  } catch (err) {
    console.error('fetchTemplateContent error:', err)
    return {}
  }
}

// ── Saved (platform) template actions — org-scoped ──

export const fetchSavedTemplates = (orgId = 'rolzo', channel) => async () => {
  try {
    let url = `templates?orgId=${orgId}`
    if (channel) url += `&channel=${channel}`
    const result = await mailFetch(url)
    return result.data || []
  } catch (err) {
    console.error('fetchSavedTemplates error:', err)
    return []
  }
}

export const createSavedTemplate = data => async () => {
  try {
    const result = await mailFetch('templates', {
      method: 'POST',
      body: JSON.stringify(data),
    })
    return result.data
  } catch (err) {
    console.error('createSavedTemplate error:', err)
    throw err
  }
}

export const updateSavedTemplate = (templateId, data) => async () => {
  try {
    const result = await mailFetch(`templates/${templateId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    })
    return result.data
  } catch (err) {
    console.error('updateSavedTemplate error:', err)
    throw err
  }
}

export const deleteSavedTemplate = templateId => async () => {
  try {
    await mailFetch(`templates/${templateId}`, { method: 'DELETE' })
  } catch (err) {
    console.error('deleteSavedTemplate error:', err)
    throw err
  }
}

// ── Log / media actions ──

export const fetchMailLogs = threadId => async () => {
  try {
    const result = await mailFetch(`threads/${threadId}/logs`)
    return result.data || []
  } catch (err) {
    console.error('fetchMailLogs error:', err)
    return []
  }
}

export const fetchMailMedia = threadId => async () => {
  try {
    const result = await mailFetch(`threads/${threadId}/media`)
    return result.data || []
  } catch (err) {
    console.error('fetchMailMedia error:', err)
    return []
  }
}

// ── Account management ──

export const fetchMailAccounts = (orgId, channel) => async dispatch => {
  try {
    let url = `accounts?orgId=${orgId}`
    if (channel) url += `&channel=${channel}`
    const result = await mailFetch(url)
    const accounts = result.data || []
    dispatch({ type: MAIL_SET_ACCOUNTS, payload: accounts })
    return accounts
  } catch (err) {
    console.error('fetchMailAccounts error:', err)
    return []
  }
}

export const startGmailConnect = (
  orgId,
  userId,
  label,
  channel
) => async () => {
  try {
    const result = await mailFetch('connect/gmail/start', {
      method: 'POST',
      body: JSON.stringify({ orgId, userId, label, channel }),
    })
    const url = result.data?.url || result.authUrl
    if (url) {
      window.open(url, '_blank', 'width=600,height=700')
    }
    return result
  } catch (err) {
    console.error('startGmailConnect error:', err)
    throw err
  }
}

export const setAccountManagerOpen = open => ({
  type: MAIL_SET_ACCOUNT_MANAGER_OPEN,
  payload: open,
})

/**
 * Build a URL to download an attachment via the gateway proxy.
 */
export const getAttachmentUrl = (
  messageId,
  attachmentId,
  accountId,
  filename
) => {
  const params = new URLSearchParams({ accountId, apiKey: MAIL_API_KEY })
  if (filename) params.set('filename', filename)
  return `${mailGatewayHost}/attachments/${messageId}/${attachmentId}?${params.toString()}`
}

export const fetchAccountSignature = accountId => async () => {
  try {
    const result = await mailFetch(`accounts/${accountId}/signature`)
    return (result.data && result.data.signature) || ''
  } catch (err) {
    console.error('fetchAccountSignature error:', err)
    return ''
  }
}

export const syncMailAccount = (accountId, full = false) => async dispatch => {
  try {
    const result = await mailFetch(`accounts/${accountId}/sync`, {
      method: 'POST',
      body: JSON.stringify({ full }),
    })
    return result.data
  } catch (err) {
    console.error('syncMailAccount error:', err)
    throw err
  }
}

export const updateMailAccount = (accountId, patch) => async dispatch => {
  try {
    const result = await mailFetch(`accounts/${accountId}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    })
    return result.data
  } catch (err) {
    console.error('updateMailAccount error:', err)
    throw err
  }
}

export const disconnectMailAccount = accountId => async dispatch => {
  try {
    const result = await mailFetch(`accounts/${accountId}`, {
      method: 'DELETE',
    })
    return result.data
  } catch (err) {
    console.error('disconnectMailAccount error:', err)
    throw err
  }
}

export const purgeMailAccount = accountId => async dispatch => {
  try {
    const result = await mailFetch(`accounts/${accountId}/purge`, {
      method: 'DELETE',
    })
    return result.data
  } catch (err) {
    console.error('purgeMailAccount error:', err)
    throw err
  }
}

// ── WebSocket connection ──

let mailSocket = null
let mailReconnectTimer = null

const getMailWsUrl = () => {
  const wsProto = mailGatewayHost.replace(/^http/, 'ws')
  return `${wsProto}/ws?apiKey=${MAIL_API_KEY}`
}

export const connectMailWebSocket = () => (dispatch, getState) => {
  if (!isMailEnabled()) return
  if (mailSocket && mailSocket.readyState <= 1) return

  const ws = new WebSocket(getMailWsUrl())
  mailSocket = ws

  ws.onopen = () => {
    dispatch({ type: MAIL_WS_CONNECTED })
    if (mailReconnectTimer) {
      clearInterval(mailReconnectTimer)
      mailReconnectTimer = null
    }
  }

  ws.onclose = () => {
    dispatch({ type: MAIL_WS_DISCONNECTED })
    mailSocket = null
    if (!mailReconnectTimer) {
      mailReconnectTimer = setInterval(() => {
        dispatch(connectMailWebSocket())
      }, 5000)
    }
  }

  ws.onerror = () => {
    // onclose will fire after onerror
  }

  ws.onmessage = event => {
    try {
      const msg = JSON.parse(event.data)
      dispatch(handleMailWebSocketEvent(msg))
    } catch (err) {
      console.error('Mail WS parse error:', err)
    }
  }
}

export const disconnectMailWebSocket = () => () => {
  if (mailReconnectTimer) {
    clearInterval(mailReconnectTimer)
    mailReconnectTimer = null
  }
  if (mailSocket) {
    mailSocket.onclose = null
    mailSocket.close()
    mailSocket = null
  }
}

const handleMailWebSocketEvent = msg => (dispatch, getState) => {
  const { mail } = getState()

  switch (msg.event) {
    case 'mail:message': {
      const { message, threadId, channel: msgChannel } = msg.data
      const activeChannel = getState().whatsapp.activeChannel
      // Skip messages for threads that don't match the current channel
      if (activeChannel && msgChannel && msgChannel !== activeChannel) {
        dispatch(fetchMailUnreadCounts('rolzo'))
        break
      }
      if (mail.selectedThread && mail.selectedThread._id === threadId) {
        const exists = mail.messages.some(m => m._id === message._id)
        if (!exists) {
          dispatch({ type: MAIL_APPEND_MESSAGE, payload: message })
        }
      }
      // Refresh threads scoped to the active channel
      dispatch(
        fetchMailThreads({
          orgId: 'rolzo',
          limit: 50,
          ...(activeChannel && { channel: activeChannel }),
        })
      )
      dispatch(fetchMailUnreadCounts('rolzo'))
      break
    }

    case 'mail:thread:update': {
      const activeChannel = getState().whatsapp.activeChannel
      // Skip thread updates from other channels
      if (
        activeChannel &&
        msg.data.channel &&
        msg.data.channel !== activeChannel
      ) {
        dispatch(fetchMailUnreadCounts('rolzo'))
        break
      }
      dispatch({ type: MAIL_UPDATE_THREAD, payload: msg.data })
      dispatch(fetchMailUnreadCounts('rolzo'))
      break
    }

    case 'mail:thread:delete': {
      dispatch({ type: MAIL_DELETE_THREAD, payload: msg.data._id })
      break
    }

    default:
      break
  }
}
